'use client'

import { useRef } from "react"
import Image from "next/image"
import ProductCard from "../product/ProductCard"
import { ArrowUp, Calendar, ChevronRight, Loader2, PackageOpen, Tag } from "lucide-react"
import { motion } from "framer-motion"
import { useInfiniteProducts } from "@/hooks/use-infinite-products"
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll"
import { useCountdown } from "@/hooks/use-countdown"
import { useProductsByIds, useCategoryExpander } from "@/lib/commerce/hooks"
import { Button } from "@/components/elements/Button"
import Skeleton from "@/components/elements/Skeleton"
import EmptyState from "@/components/elements/EmptyState"
import { formatImageURLGoogle, getAspectRatioClass } from "@/utils/util"
import { getCampaignDiscountInfo, getCampaignTargetInfo, getCampaignStatus } from "@/utils/campaign"
import moment from "moment"

const shimmer = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2YzZjRmNiIvPjwvc3ZnPg=='

const myLoader = ({ src, width, quality }: any) => `${src}?w=${width}&q=${quality || 75}`

export default function Campaign({ vendor, campaign }: { vendor: any, campaign: any, wishlist?: any }) {
  const targetInfo = getCampaignTargetInfo(campaign.targets)
  const discountInfo = getCampaignDiscountInfo(campaign.targets)
  const status = getCampaignStatus(campaign.startDate, campaign.endDate)
  const countdownEnabled = status === 'active'
  const countdown = useCountdown(campaign.endDate, countdownEnabled)

  const isWideRatio = vendor?.imageAspectRatio === '16/9'
  const gridCols = isWideRatio ? 'lg:grid-cols-4' : 'lg:grid-cols-5'
  const aspectRatioClass = getAspectRatioClass(vendor?.imageAspectRatio)

  return (
    <div>
      <div className="cont pt-4 sm:pt-6">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <a href="/" className="hover:text-foreground">Нүүр</a>
          <ChevronRight className="h-3 w-3 opacity-50" />
          <a href="/c" className="hover:text-foreground">Хямдралын аян</a>
          <ChevronRight className="h-3 w-3 opacity-50" />
          <span className="text-foreground font-medium">{campaign.name}</span>
        </div>
      </div>

      <div className="cont pt-3 sm:pt-4 pb-5 sm:pb-6">
        {campaign.campaignBanner && (
          <a href="#products" className="block relative w-full aspect-16/7 sm:aspect-9/4 rounded-2xl sm:rounded-3xl overflow-hidden bg-muted group">
            <Image loader={myLoader} src={formatImageURLGoogle(campaign.campaignBanner)} alt={campaign.name} fill className="object-cover transition-transform duration-700 group-hover:scale-[1.015]" placeholder="blur" blurDataURL={shimmer} priority />
          </a>
        )}

        <div className="mt-4 sm:mt-5 rounded-xl border border-border bg-background p-4 sm:p-5 flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-8">
          <div className="min-w-0 flex flex-col gap-2">
            <div className="flex items-center gap-2">
              {status === 'active' ? (
                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-[0.16em] bg-green-100 text-green-600">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" /> Идэвхтэй
                </div>
              ) : status === 'expired' ? (
                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-[0.16em] bg-red-100 text-red-600">
                  Дууссан
                </div>
              ) : (
                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-[0.16em] bg-muted text-muted-foreground">
                  Удахгүй
                </div>
              )}
              <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted-foreground">Хямдралын аян</div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2 text-[18px] sm:text-[20px] font-semibold tracking-[-0.0075em]">
              <span>{campaign.name}</span>
              {discountInfo && <span className="text-muted-foreground text-sm sm:text-[20px] font-medium sm:font-semibold"><span className="hidden sm:inline">· </span>{discountInfo.label} {discountInfo.suffix}</span>}
            </div>
          </div>

          {(countdown || (!countdown && campaign.startDate)) && (
            <div className="lg:hidden h-px w-full bg-border" />
          )}

          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 lg:contents">
            {countdown && (
              <>
                <div className="hidden lg:block w-px h-12 bg-border" />
                <div className="flex justify-center sm:justify-start">
                  <CountdownStrip countdown={countdown} />
                </div>
              </>
            )}

            {!countdown && campaign.startDate && (
              <>
                <div className="hidden lg:block w-px h-12 bg-border" />
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span className="font-medium">
                    {moment(campaign.startDate).format('MM.DD')} - {campaign.endDate ? moment(campaign.endDate).format('MM.DD') : '∞'}
                  </span>
                </div>
              </>
            )}

            <div className="hidden lg:block flex-1" />
            <div className="flex items-center gap-2 sm:ml-auto lg:ml-0 w-full sm:w-auto">
              {campaign.type === 'freeShipping' && (
                <span className="inline-flex items-center justify-center gap-1 bg-green-100 text-green-600 text-sm font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">
                  <Tag className="h-4 w-4" />
                  Үнэгүй хүргэлт
                </span>
              )}
              <Button href="#products" tag="a" size="default" className="w-full sm:w-auto" color={vendor?.color?.primary}>Бүгдийг харах</Button>
            </div>
          </div>
        </div>

        {campaign.description && (
          <p className="mt-4 text-muted-foreground text-sm sm:text-base leading-6">{campaign.description}</p>
        )}
      </div>

      <div id="products">
        {targetInfo.type === 'all' ? (
          <InfiniteCampaignGrid vendorId={vendor?.id} vendor={vendor} gridCols={gridCols} aspectRatioClass={aspectRatioClass} />
        ) : targetInfo.type === 'category' ? (
          <CategoryCampaignGrid vendorId={vendor?.id} categoryIds={targetInfo.categoryIds} vendor={vendor} gridCols={gridCols} aspectRatioClass={aspectRatioClass} />
        ) : targetInfo.type === 'product' ? (
          <ProductIdsCampaignGrid vendorId={vendor?.id} productIds={targetInfo.productIds} vendor={vendor} gridCols={gridCols} aspectRatioClass={aspectRatioClass} />
        ) : null}
      </div>
    </div>
  )
}

function pad2(n: number) { return String(n).padStart(2, '0') }

function CountdownStrip({ countdown }: { countdown: { d: number, h: number, m: number, s: number } }) {
  const cells = [
    { v: countdown.d, k: 'өдөр' },
    { v: countdown.h, k: 'цаг' },
    { v: countdown.m, k: 'мин' },
    { v: countdown.s, k: 'сек' },
  ]
  return (
    <div className="inline-flex flex-col items-start">
      <div className="mb-1.5 font-medium tracking-[0.18em] uppercase text-muted-foreground text-[9px]">Дуусахад</div>
      <div className="flex items-end gap-1.5">
        {cells.map((c, i) => (
          <div key={c.k} className="flex items-end gap-1.5">
            <div className="flex flex-col items-center">
              <div className="rounded-lg font-bold tabular-nums tracking-[-0.02em] grid place-items-center min-w-10 px-2 py-1.5 text-[15px] bg-background text-foreground border border-border shadow-sm">
                {pad2(c.v)}
              </div>
              <div className="mt-1 font-medium tracking-[0.16em] uppercase text-muted-foreground text-[9px]">{c.k}</div>
            </div>
            {i < cells.length - 1 && <div className="pb-6 font-bold opacity-30 text-[15px]">:</div>}
          </div>
        ))}
      </div>
    </div>
  )
}

function CampaignProductGrid({
  products, isLoading, isLoadingMore, total, hasMore, loadMore,
  vendor, gridCols, aspectRatioClass, skeletonCount,
}: {
  products: any[], isLoading: boolean, isLoadingMore?: boolean, total: number, hasMore?: boolean,
  loadMore?: () => void, vendor: any, gridCols: string, aspectRatioClass: string, skeletonCount: number,
}) {
  const loadMoreRef = useRef<HTMLDivElement>(null)
  useInfiniteScroll({
    targetRef: loadMoreRef,
    onLoadMore: () => loadMore?.(),
    enabled: !!hasMore && !!loadMore && !isLoading && !isLoadingMore,
  })

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' })

  return (
    <motion.div initial={{ opacity: 0, y: -10 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0, duration: 0.5, ease: "easeIn" }} viewport={{ once: true }} className="cont pb-4 sm:pb-8">
      <div className="flex items-end justify-between gap-4 mb-4">
        <div>
          <div className="text-foreground text-lg sm:text-xl font-semibold tracking-[-0.0075em]">Хямдралын аянд хамрах бүтээгдэхүүн</div>
          {!isLoading && (
            <div className="mt-1 text-sm text-muted-foreground">{total} бүтээгдэхүүн</div>
          )}
        </div>
      </div>
      {isLoading ? (
        <div className={`grid grid-cols-2 md:grid-cols-4 ${gridCols} gap-x-4 gap-y-10 mt-4`}>
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <SkeletonProduct key={index} aspectRatioClass={aspectRatioClass} />
          ))}
        </div>
      ) : products.length > 0 ? (
        <>
          <div className={`grid grid-cols-2 md:grid-cols-4 ${gridCols} gap-x-4 gap-y-10 mt-4`}>
            {products.map((product: any, index: number) => (
              <motion.div key={product.customId || product.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index * 0.05, 0.5), duration: 0.5, ease: "easeOut" }} viewport={{ once: true }}>
                <ProductCard product={product} vendor={vendor} />
              </motion.div>
            ))}
          </div>
          {loadMore && (
            <div ref={loadMoreRef} className="w-full pt-12 flex justify-center">
              {isLoadingMore && (
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Ачаалж байна...</span>
                </div>
              )}
              {!hasMore && products.length > 0 && (
                <div className="flex flex-col items-center gap-4">
                  <span className="text-sm text-muted-foreground">Бүх бүтээгдэхүүн ачаалагдлаа ({total})</span>
                  <Button onClick={scrollToTop} variant="outline" size="sm" icon={ArrowUp}>Дээш буцах</Button>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <EmptyState icon={PackageOpen} title="Бүтээгдэхүүн байхгүй байна" description="Энэ урамшуулалд хамаарах бүтээгдэхүүн байхгүй байна." />
      )}
    </motion.div>
  )
}

function InfiniteCampaignGrid({ vendorId, vendor, gridCols, aspectRatioClass }: { vendorId: string, vendor: any, gridCols: string, aspectRatioClass: string }) {
  const { products, isLoading, isLoadingMore, metadata, loadMore, hasMore } = useInfiniteProducts({ vendorId, limit: 20 })
  return (
    <CampaignProductGrid
      products={products} isLoading={isLoading} isLoadingMore={isLoadingMore}
      total={metadata.total} hasMore={hasMore} loadMore={loadMore}
      vendor={vendor} gridCols={gridCols} aspectRatioClass={aspectRatioClass} skeletonCount={10}
    />
  )
}

function CategoryCampaignGrid({ vendorId, categoryIds, vendor, gridCols, aspectRatioClass }: { vendorId: string, categoryIds: string[], vendor: any, gridCols: string, aspectRatioClass: string }) {
  const { expandedIds, loaded } = useCategoryExpander(vendorId, categoryIds)

  if (!loaded) {
    return (
      <div className="cont pb-4 sm:pb-8">
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="text-foreground text-lg sm:text-xl font-semibold">Бүтээгдэхүүн</div>
        </div>
        <div className={`grid grid-cols-2 md:grid-cols-4 ${gridCols} gap-x-4 gap-y-10 mt-4`}>
          {Array.from({ length: 10 }).map((_, index) => (
            <SkeletonProduct key={index} aspectRatioClass={aspectRatioClass} />
          ))}
        </div>
      </div>
    )
  }

  return <CategoryCampaignGridLoaded vendorId={vendorId} expandedIds={expandedIds} vendor={vendor} gridCols={gridCols} aspectRatioClass={aspectRatioClass} />
}

function CategoryCampaignGridLoaded({ vendorId, expandedIds, vendor, gridCols, aspectRatioClass }: { vendorId: string, expandedIds: string, vendor: any, gridCols: string, aspectRatioClass: string }) {
  const { products, isLoading, isLoadingMore, metadata, loadMore, hasMore } = useInfiniteProducts({
    vendorId, limit: 20, initialFilters: { category: expandedIds },
  })
  return (
    <CampaignProductGrid
      products={products} isLoading={isLoading} isLoadingMore={isLoadingMore}
      total={metadata.total} hasMore={hasMore} loadMore={loadMore}
      vendor={vendor} gridCols={gridCols} aspectRatioClass={aspectRatioClass} skeletonCount={10}
    />
  )
}

function ProductIdsCampaignGrid({ vendorId, productIds, vendor, gridCols, aspectRatioClass }: { vendorId: string, productIds: string[], vendor: any, gridCols: string, aspectRatioClass: string }) {
  const { products, isLoading } = useProductsByIds(vendorId, productIds)
  return (
    <CampaignProductGrid
      products={products} isLoading={isLoading}
      total={products.length}
      vendor={vendor} gridCols={gridCols} aspectRatioClass={aspectRatioClass} skeletonCount={productIds.length}
    />
  )
}

function SkeletonProduct({ aspectRatioClass }: { aspectRatioClass?: string }) {
  const defaultAspectRatio = aspectRatioClass || 'aspect-w-1 aspect-h-1'
  return (
    <span className="group text-sm cursor-pointer">
      <div className={`${defaultAspectRatio} w-full relative overflow-hidden rounded-lg`}>
        <Skeleton className="absolute inset-0 w-full h-full" />
      </div>
      <div className="grid xl:hidden w-full grid-cols-2 gap-2 mt-2 h-7">
        <Skeleton className="w-full h-full rounded" />
        <Skeleton className="w-full h-full rounded" />
      </div>
      <div className="flex flex-col text-center mt-2 space-y-2">
        <Skeleton className="h-5 w-full rounded" />
        <Skeleton className="h-4 w-3/4 mx-auto rounded" />
        <Skeleton className="h-4 w-1/2 mx-auto rounded" />
      </div>
    </span>
  )
}
